import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { ApiError, AuthUser } from '@lms/shared';

/**
 * The access token lives in memory only — never localStorage, where an XSS
 * payload could read it. It is lost on refresh, and recovered on boot by
 * calling /auth/refresh with the httpOnly cookie the browser still holds.
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export const api: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  /**
   * Long enough to survive a cold start.
   *
   * This was 30s, which is a sensible ceiling for a server that is already
   * running and the wrong number for one that sleeps. A free-tier host spins
   * the service down after a few minutes idle, and waking it — container,
   * then the database, which sleeps too — takes longer than thirty seconds.
   *
   * The client gave up first, so the very first action after any quiet period
   * failed with "Something went wrong": no response had arrived, so there was
   * no message from the server to show. Signing in looked broken when the
   * password was never even checked.
   *
   * Only the first request after a sleep is slow; everything after it answers
   * in tens of milliseconds. Dropping back to 30s is right the moment this
   * runs somewhere that stays awake.
   */
  timeout: 75_000,
});

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

/**
 * Single-flight refresh: if several requests 401 at once, only one hits
 * /auth/refresh and the rest wait for its result. Without this, a dashboard
 * firing six parallel queries would rotate the refresh token six times and
 * invalidate its own session.
 */
let refreshPromise: Promise<string | null> | null = null;

type OnUnauthenticated = () => void;
let onUnauthenticated: OnUnauthenticated = () => {};

export function setUnauthenticatedHandler(handler: OnUnauthenticated): void {
  onUnauthenticated = handler;
}

async function refreshAccessToken(): Promise<string | null> {
  try {
    const { data } = await axios.post<{ accessToken: string; user: AuthUser }>(
      '/api/v1/auth/refresh',
      {},
      { withCredentials: true },
    );
    setAccessToken(data.accessToken);
    return data.accessToken;
  } catch {
    setAccessToken(null);
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ error?: ApiError }>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    const status = error.response?.status;

    const isAuthRoute = original?.url?.includes('/auth/login') || original?.url?.includes('/auth/refresh');

    if (status === 401 && original && !original._retried && !isAuthRoute) {
      original._retried = true;

      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null;
      });

      const token = await refreshPromise;
      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      }

      onUnauthenticated();
    }

    return Promise.reject(error);
  },
);

/**
 * Pulls the server's structured error out of an axios failure.
 *
 * A validation failure arrives as the generic "Validation failed" plus a
 * `details` map of which field was wrong and why. Showing only the headline
 * put "Validation failed" in a toast and threw away the one part that told
 * anybody what to do about it — so the field messages are folded into the
 * message here, once, for every form in the app.
 */
export function toApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    const payload = (error.response?.data as { error?: ApiError } | undefined)?.error;
    if (payload) {
      const fields = Object.entries(payload.details ?? {})
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join(' · ');

      return fields ? { ...payload, message: `${payload.message} — ${fields}` } : payload;
    }
    if (error.code === 'ECONNABORTED') {
      return { code: 'TIMEOUT', message: 'The request timed out. Please try again.' };
    }
    if (!error.response) {
      return { code: 'NETWORK', message: 'Cannot reach the server. Check your connection.' };
    }
  }
  return { code: 'UNKNOWN', message: 'Something went wrong. Please try again.' };
}

export { refreshAccessToken };
